import { PropsWithChildren, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { createStrictContext } from '.'
import store, { AllState } from '../../store'
import { BaseWbsItemType, loginToProjectByCode } from '../../store/project'
import { ProjectDetail } from '../../lib/functions/project'
import { useSelector } from 'react-redux'
import { WbsItemTypeVO } from '../../domain/value-object/WbsItemTypeVO'

type PathProps = {
  code?: string
}

type ProjectPrivateContext = {
  project: ProjectDetail
  // TODO Remove it
  projectUuid: string
  projectCode: string | undefined
  wbsItemTypes: BaseWbsItemType
  ticketListTypes: WbsItemTypeVO[]
  ticketTypes: WbsItemTypeVO[]
}

const [useContext, Provider] = createStrictContext<ProjectPrivateContext>()

export const useProjectPrivateContext = useContext

// Selectors for individual pieces of state
const selectProject = (state: AllState) => state.project.current
const selectProjectUuid = (state: AllState) => state.project.selected
const selectWbsItemTypes = (state: AllState) => state.project.wbsItemTypes
const selectTicketListTypes = (state: AllState) => state.project.ticketListTypes
const selectTicketTypes = (state: AllState) => state.project.ticketTypes
const selectIsFirstFunctionLayer = (state: AllState) =>
  state.functionLayer.currentDepth === 0

// Deep comparison for arrays of WbsItemTypeVO
const areTicketTypesEqual = (
  a: WbsItemTypeVO[] | undefined,
  b: WbsItemTypeVO[] | undefined
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false
  return a.every((item, index) => item.uuid === b[index].uuid)
}

// Custom equality for wbsItemTypes (BaseWbsItemType)
const areWbsItemTypesEqual = (
  a: BaseWbsItemType | undefined,
  b: BaseWbsItemType | undefined
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  const aTypes = a.getAll()
  const bTypes = b.getAll()
  if (aTypes.length !== bTypes.length) return false
  return aTypes.every((item, index) => item.uuid === bTypes[index]?.uuid)
}

// Custom equality for project
const areProjectsEqual = (
  a: ProjectDetail | undefined,
  b: ProjectDetail | undefined
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return a.uuid === b.uuid && a.lockVersion === b.lockVersion
}

// Compare two context values for equality
const areContextValuesEqual = (
  prev: ProjectPrivateContext | undefined,
  next: ProjectPrivateContext | undefined
): boolean => {
  if (prev === next) return true
  if (!prev || !next) return false
  return (
    areProjectsEqual(prev.project, next.project) &&
    prev.projectUuid === next.projectUuid &&
    prev.projectCode === next.projectCode &&
    areWbsItemTypesEqual(prev.wbsItemTypes, next.wbsItemTypes) &&
    areTicketTypesEqual(prev.ticketListTypes, next.ticketListTypes) &&
    areTicketTypesEqual(prev.ticketTypes, next.ticketTypes)
  )
}

// Custom hook that returns a stable reference if values haven't changed
const useStableContextValue = (
  project: ProjectDetail | undefined,
  projectUuid: string | undefined,
  projectCode: string | undefined,
  wbsItemTypes: BaseWbsItemType,
  ticketListTypes: WbsItemTypeVO[],
  ticketTypes: WbsItemTypeVO[],
  isFirstFunctionLayer: boolean,
  code: string | undefined
): ProjectPrivateContext | undefined => {
  const prevValueRef = useRef<ProjectPrivateContext | undefined>(undefined)

  // Check if we should return undefined
  if (
    !project ||
    !projectUuid ||
    (isFirstFunctionLayer && projectCode !== code)
  ) {
    prevValueRef.current = undefined
    return undefined
  }

  // Create the new value
  const newValue: ProjectPrivateContext = {
    project,
    projectUuid,
    projectCode,
    wbsItemTypes,
    ticketListTypes,
    ticketTypes,
  }

  // If previous value exists and is equal, return the previous reference
  if (areContextValuesEqual(prevValueRef.current, newValue)) {
    return prevValueRef.current
  }

  // Otherwise, update the ref and return the new value
  prevValueRef.current = newValue
  return newValue
}

export const ProjectProvider = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const { code } = useParams<PathProps>()

  // Use individual selectors with proper equality checks
  const project = useSelector(selectProject, areProjectsEqual)
  const projectUuid = useSelector(selectProjectUuid)
  const wbsItemTypes = useSelector(selectWbsItemTypes, areWbsItemTypesEqual)
  const ticketListTypes = useSelector(
    selectTicketListTypes,
    areTicketTypesEqual
  )
  const ticketTypes = useSelector(selectTicketTypes, areTicketTypesEqual)
  const isFirstFunctionLayer = useSelector(selectIsFirstFunctionLayer)

  const projectCode = project?.code

  // Get stable context value
  const projectPrivateProps = useStableContextValue(
    project,
    projectUuid,
    projectCode,
    wbsItemTypes,
    ticketListTypes,
    ticketTypes,
    isFirstFunctionLayer,
    code
  )

  const fetchProject = useCallback(async () => {
    if (!code) return undefined
    store.dispatch(loginToProjectByCode(code))
  }, [code])

  useEffect(() => {
    if (
      !project ||
      !projectUuid ||
      (isFirstFunctionLayer && projectCode !== code)
    ) {
      fetchProject()
    }
  }, [
    project,
    projectUuid,
    projectCode,
    code,
    fetchProject,
    isFirstFunctionLayer,
  ])

  if (!projectPrivateProps) return <></>

  return <Provider value={projectPrivateProps}>{children}</Provider>
}
